import React, { useRef, useState, useEffect } from "react";
import StatusDropdown from "../../Shared/StatusDropdown";
import FullPageLoader from "../../../common/FullPageLoader";
import { useNavigate } from "react-router-dom";

export default function FontForm({
  initialValues,
  onSubmit,
  loading = false,
  buttonText = "Submit",
}) {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    value: null,
    status: "ACTIVE",
  });

  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");

  // Load edit data
  useEffect(() => {
    if (initialValues) {
      setFormData({
        name: initialValues.name || "",
        value: null,
        status: initialValues.status || "ACTIVE",
      });
    }
  }, [initialValues]);

  // Convert file to Base64
  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();

      reader.readAsDataURL(file);

      reader.onload = () => {
        resolve(reader.result);
      };

      reader.onerror = (error) => reject(error);
    });
  };

  // Handle input
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  // Handle file
  const handleFileChange = (e) => {
    const file = e.target.files?.[0];

    if (!file) return;

    const allowedExtensions = ["ttf", "otf", "woff", "woff2"];
    const extension = file.name.split(".").pop().toLowerCase();

    if (!allowedExtensions.includes(extension)) {
      setErrors((prev) => ({
        ...prev,
        file: "Only .ttf, .otf, .woff and .woff2 files are allowed.",
      }));

      e.target.value = "";
      return;
    }

    setFormData((prev) => ({
      ...prev,
      file,
    }));

    setErrors((prev) => ({
      ...prev,
      file: "",
    }));
  };

  const handleClearFile = () => {
    setFormData((prev) => ({ ...prev, file: null }));
    setErrors((prev) => ({ ...prev, file: "" }));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  // Submit
  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = {};

    if (!formData.name.trim()) {
      validationErrors.name = "Font name is required";
    }

    if (!initialValues && !formData.file) {
      validationErrors.file = "Font file is required";
    }

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    try {
      setErrors({});
      setSuccessMessage("");

      let fileBase64 = "";

      if (formData.file) {
        fileBase64 = await convertToBase64(formData.file);
      }

      const payload = {
        name: formData.name.trim(),
        status: formData.status,
        value: fileBase64,
      };

      const res = await onSubmit(payload);

      if (res?.status === 422 || res?.success === false) {
        setErrors(res.error || {});
        return;
      }

      setSuccessMessage(
        initialValues
          ? "Font Updated Successfully"
          : "Font Added Successfully"
      );

      if (!initialValues) {
        setFormData({
          name: "",
          initialValues: null,
          status: "ACTIVE",
        });
      }

      setTimeout(() => {
        navigate("/font");
      }, 1500);
    } catch (err) {
      const apiErrors = err?.response?.data?.error;

      if (apiErrors) {
        setErrors(apiErrors);
      } else {
        console.error(err);
      }
    }
  };

  return (
    <div>
      {loading && <FullPageLoader text="Saving..." />}
      {successMessage && (
        <div className="alert alert-success">{successMessage}</div>
      )}

      <form onSubmit={handleSubmit}>
        <div className="row g-2">
          {/* Font Name */}
          <div className="col-md-6">
            <label>
              Font name<span className="error">*</span>
            </label>

            <input
              type="text"
              name="name"
              className={`form-control ${errors.name ? "is-invalid" : ""}`}
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter font name"
            />

            {errors.name && (
              <div className="invalid-feedback">{errors.name}</div>
            )}
          </div>

          {/* Font File */}
          <div className="col-md-6">
            <label>
              Font file<span className="error">*</span>
            </label>

            <input
              type="file"
              ref={fileInputRef}
              name="file"
              className={`form-control ${errors.file ? "is-invalid" : ""}`}
              accept=".ttf,.otf,.woff,.woff2"
              onChange={handleFileChange}
            />

            {formData.file && (
              <div className="d-flex align-items-center gap-2 mt-1">
                <small className="text-success">{formData.file.name}</small>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  aria-label="Remove selected font file"
                  onClick={handleClearFile}
                >
                  &times;
                </button>
              </div>
            )}

            {errors.file && (
              <div className="invalid-feedback">{errors.file}</div>
            )}
          </div>

          {/* Status */}
          <div className="col-md-6">
            <label>
              Status<span className="error">*</span>
            </label>

            <StatusDropdown
              className={`form-select ${errors.status ? "is-invalid" : ""}`}
              name="status"
              value={formData.status}
              onChange={handleChange}
             activeLabel="ACTIVE" inactiveLabel="INACTIVE" />

            {errors.status && (
              <div className="invalid-feedback">{errors.status}</div>
            )}
          </div>

          {/* Buttons */}
          <div className="col-12 d-flex justify-content-end gap-2">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? "Loading..." : buttonText}
            </button>

            <button
              type="button"
              className="btn"
              style={{ backgroundColor: "#032B4D", color: "#fff" }}
              onClick={() => navigate("/font")}
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    </div>
  );
}
